import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';

import CustomButton from '../../../components/button/ButtonComponent';
import { colors } from '../../../components/theme/theme';
import {
    getPasswordHintMessage,
    getStrengthLevel,
    LEVEL_CONFIG,
} from '../../../components/ui-components/LoginPasswordStrengthField';
import { useTranslation } from '../../../utils/translations';

import { IdentifierType, ParsedIdentifier, parseIdentifierInput, validateIdentifier } from './identifier';
import { formatPhoneForDisplay, LOGIN_LIMITS, validatePassword } from './validators';

export type LoginIdentifierMode = 'email' | 'phone';

interface MergedLoginFormProps {
    mode: LoginIdentifierMode;
    email: string;
    /** National (10-digit) phone number, unformatted. */
    phone: string;
    password: string;
    /** Fired on every edit of the single email/mobile field with the detected type and cleaned value. */
    onIdentifierChange: (parsed: ParsedIdentifier) => void;
    onPasswordChange: (value: string) => void;
    onSubmit: () => void;
    onForgotPassword: () => void;
    onUpdatePhone?: () => void;
    submitDisabled?: boolean;
    /** Server reported the mobile number as not registered/verified. */
    phoneNotRegistered?: boolean;
    onVerifyMobile: () => void;
}

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 3,
        backgroundColor: 'common.white',
        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.06)',
        minHeight: { xs: 48, sm: 52 },
        '& fieldset': { borderColor: colors.greyPreset[20] },
        '&:hover fieldset': { borderColor: colors.bluePreset[60] },
    },
    '& .MuiInputBase-input::placeholder': { opacity: 0.6 },
};

const labelSx = { fontWeight: 600, color: colors.bluePreset[10], mb: 0.75 };

const linkActionSx = {
    cursor: 'pointer',
    fontWeight: 600,
    color: colors.bluePreset[10],
    '&:hover': { textDecoration: 'underline' },
};

// CustomButton forces `boxShadow: 'none'` after sx, so the doubled selector (`&&`) is needed to win.
// Gradient/shadow apply only when enabled, leaving CustomButton's own disabled styling intact.
const loginButtonSx = {
    minHeight: { xs: 48, sm: 52 },
    borderRadius: 3,
    '&&:not(.Mui-disabled)': {
        background: `linear-gradient(90deg, ${colors.bluePreset[10]} 0%, color-mix(in srgb, ${colors.bluePreset[10]} 78%, ${colors.bluePreset[30]}) 100%)`,
        boxShadow: `0 8px 18px ${alpha(colors.bluePreset[10], 0.28)}`,
    },
};

/** What the single field currently holds, derived from the parent's email/phone state. */
export const getIdentifierType = (mode: LoginIdentifierMode, email: string, phone: string): IdentifierType => {
    if (mode === 'phone') return phone ? 'phone' : 'unknown';
    return email ? 'email' : 'unknown';
};

export const isMergedLoginValid = (mode: LoginIdentifierMode, email: string, phone: string, password: string) =>
    validateIdentifier(getIdentifierType(mode, email, phone), email, phone) === null &&
    validatePassword(password) === null;

export const MergedLoginForm: React.FC<MergedLoginFormProps> = ({
    mode,
    email,
    phone,
    password,
    onIdentifierChange,
    onPasswordChange,
    onSubmit,
    onForgotPassword,
    onUpdatePhone,
    submitDisabled,
    phoneNotRegistered,
    onVerifyMobile,
}) => {
    const { t } = useTranslation();
    const [touched, setTouched] = useState({ identifier: false, password: false });
    const [showPassword, setShowPassword] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);

    // Password visibility always resets to hidden when the tab loses visibility.
    useEffect(() => {
        const hide = () => setShowPassword(false);
        document.addEventListener('visibilitychange', hide);
        window.addEventListener('blur', hide);
        return () => {
            document.removeEventListener('visibilitychange', hide);
            window.removeEventListener('blur', hide);
        };
    }, []);

    useEffect(() => {
        if (phoneNotRegistered) panelRef.current?.focus();
    }, [phoneNotRegistered]);

    const type = getIdentifierType(mode, email, phone);
    const identifierValue = type === 'phone' ? formatPhoneForDisplay(phone) : email;
    const showPhoneServerError = type === 'phone' && !!phoneNotRegistered;
    const identifierFormatError = touched.identifier ? validateIdentifier(type, email, phone) : null;
    const identifierError = showPhoneServerError ? t('authFlow.phoneNotRegistered.heading') : identifierFormatError;
    const passwordError = touched.password ? validatePassword(password) : null;
    const strengthLevel = getStrengthLevel(password);
    const { label: strengthLabel, color: strengthColor } = LEVEL_CONFIG[strengthLevel];
    const passwordHint = getPasswordHintMessage(password);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (submitDisabled) return;
        setTouched({ identifier: true, password: password === '' });
        onSubmit();
    };

    return (
        <Stack component="form" noValidate onSubmit={handleSubmit} gap={2} width="100%">
            <Box>
                <Typography variant="subtitle2" aria-hidden sx={labelSx}>
                    {t('authFlow.login.identifierLabel')}
                </Typography>
                <TextField
                    fullWidth
                    value={identifierValue}
                    placeholder={t('authFlow.login.identifierPlaceholder')}
                    error={!!identifierError}
                    helperText={showPhoneServerError ? undefined : (identifierFormatError ?? undefined)}
                    onChange={e => onIdentifierChange(parseIdentifierInput(e.target.value, type))}
                    onBlur={() => setTouched(t => ({ ...t, identifier: true }))}
                    autoComplete="username"
                    inputProps={{
                        'aria-label': 'Email or Phone Number',
                        inputMode: type === 'phone' ? 'tel' : 'email',
                        maxLength: type === 'phone' ? 14 : LOGIN_LIMITS.EMAIL_MAX_LENGTH,
                        autoCapitalize: 'none',
                        spellCheck: false,
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start" sx={{ color: 'text.secondary', mr: 1 }}>
                                {type === 'phone' ? (
                                    <PhoneIphoneIcon fontSize="small" />
                                ) : (
                                    <MailOutlineIcon fontSize="small" />
                                )}
                                {type === 'phone' && (
                                    <Typography component="span" color="text.primary" sx={{ ml: 1 }}>
                                        +1
                                    </Typography>
                                )}
                            </InputAdornment>
                        ),
                        endAdornment: showPhoneServerError ? (
                            <InputAdornment position="end">
                                <ErrorOutlineIcon color="error" />
                            </InputAdornment>
                        ) : undefined,
                    }}
                    sx={{
                        ...fieldSx,
                        ...(showPhoneServerError && {
                            '& .MuiOutlinedInput-root': {
                                ...fieldSx['& .MuiOutlinedInput-root'],
                                '& fieldset': { borderColor: 'error.dark', borderWidth: 1.5 },
                                '&:hover fieldset': { borderColor: 'error.dark' },
                            },
                        }),
                    }}
                />
                {showPhoneServerError && (
                    <Box
                        ref={panelRef}
                        tabIndex={-1}
                        role="alert"
                        aria-live="assertive"
                        sx={{ mt: 1.5, p: 2, borderRadius: 3, bgcolor: 'rgba(211, 47, 47, 0.07)', outline: 'none' }}
                    >
                        <Stack direction="row" gap={1.25} alignItems="center" mb={0.5}>
                            <WarningAmberIcon color="error" />
                            <Typography variant="subtitle1" color="error.dark">
                                {t('authFlow.phoneNotRegistered.heading')}
                            </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            {t('authFlow.phoneNotRegistered.body')}
                        </Typography>
                        <CustomButton
                            variant="outlined"
                            borderColor={colors.errorPreset[10]}
                            textColor={colors.errorPreset[10]}
                            startIcon={<VerifiedUserOutlinedIcon />}
                            onClick={onVerifyMobile}
                        >
                            {`${t('authFlow.phoneNotRegistered.cta')} →`}
                        </CustomButton>
                    </Box>
                )}
            </Box>

            <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1} mb={0.75}>
                    <Typography variant="subtitle2" aria-hidden sx={{ ...labelSx, mb: 0 }}>
                        {t('authFlow.login.passwordLabel')}
                    </Typography>
                    <Typography
                        component="span"
                        role="button"
                        tabIndex={0}
                        variant="body2"
                        onClick={onForgotPassword}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onForgotPassword();
                            }
                        }}
                        sx={{ ...linkActionSx, whiteSpace: 'nowrap' }}
                    >
                        {t('authFlow.login.forgotPassword')}
                    </Typography>
                </Stack>
                <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    placeholder={t('authFlow.login.passwordPlaceholder')}
                    error={!!passwordError}
                    helperText={passwordError ?? undefined}
                    onChange={e => {
                        const next = e.target.value.slice(0, LOGIN_LIMITS.PASSWORD_MAX_LENGTH);
                        // Only a user-emptied field counts as "missing"; a programmatic clear after a failed login does not.
                        if (next === '') setTouched(t => ({ ...t, password: true }));
                        onPasswordChange(next);
                    }}
                    onBlur={() => setTouched(t => ({ ...t, password: password === '' }))}
                    autoComplete="current-password"
                    inputProps={{ 'aria-label': 'Password', maxLength: LOGIN_LIMITS.PASSWORD_MAX_LENGTH }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start" sx={{ color: 'text.secondary' }}>
                                <LockOutlinedIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    onClick={() => setShowPassword(s => !s)}
                                    onMouseDown={e => e.preventDefault()}
                                >
                                    {showPassword ? (
                                        <VisibilityOff fontSize="small" />
                                    ) : (
                                        <Visibility fontSize="small" />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={fieldSx}
                />
            </Box>

            {password.length > 0 && (
                <Box data-testid="password-strength" sx={{ mt: -1 }}>
                    <Box display="flex" gap={0.75}>
                        {[1, 2, 3].map(segment => (
                            <Box
                                key={segment}
                                sx={{
                                    flex: 1,
                                    height: 4,
                                    borderRadius: 2,
                                    backgroundColor: segment <= strengthLevel ? strengthColor : colors.greyPreset[20],
                                    transition: 'background-color 0.2s ease',
                                }}
                            />
                        ))}
                    </Box>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        gap={1}
                        mt={0.5}
                        role="status"
                        aria-live="polite"
                    >
                        <Typography variant="caption" sx={{ color: strengthColor }}>
                            {passwordHint}
                        </Typography>
                        <Typography variant="caption" sx={{ color: strengthColor, fontWeight: 600, flexShrink: 0 }}>
                            {strengthLabel}
                        </Typography>
                    </Box>
                </Box>
            )}

            <CustomButton
                type="submit"
                variant="contained"
                disabled={submitDisabled}
                aria-label="Login"
                sx={loginButtonSx}
            >
                {`${t('authFlow.login.submit')} →`}
            </CustomButton>

            {onUpdatePhone && (
                <>
                    <Box sx={{ borderTop: 1, borderColor: colors.greyPreset[20], mt: 1.5 }} />
                    <Box textAlign="center" sx={{ mt: 1, mb: 0.5 }}>
                        <Stack
                            component="span"
                            role="button"
                            tabIndex={0}
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            gap={1}
                            onClick={onUpdatePhone}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onUpdatePhone();
                                }
                            }}
                            sx={{
                                display: 'inline-flex',
                                maxWidth: '100%',
                                cursor: 'pointer',
                                px: { xs: 1.5, sm: 2.5 },
                                py: 1,
                                borderRadius: 999,
                                border: 1,
                                borderColor: colors.bluePreset[60],
                                backgroundColor: colors.bluePreset[50],
                                color: colors.bluePreset[10],
                                '&:hover': { backgroundColor: colors.bluePreset[80] },
                            }}
                        >
                            <PhoneIphoneIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                            <Typography variant="body2" component="span" sx={{ fontWeight: 600, textAlign: 'left' }}>
                                {t('authFlow.updatePhone.link')}
                            </Typography>
                        </Stack>
                    </Box>
                </>
            )}
        </Stack>
    );
};
